import { Link } from "react-router-dom";
import { ArrowRight, LandPlot, Building2 } from "lucide-react";
import Reveal from "@/components/Reveal";

export const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const SectionHeader = ({ overline, title, description, testId, dark = false }) => (
  <Reveal className="max-w-3xl">
    <p
      className={`text-xs font-semibold uppercase tracking-[0.28em] ${dark ? "text-brand-light" : "text-brand-dark"}`}
      data-testid={testId ? `${testId}-overline` : undefined}
    >
      {overline}
    </p>
    <h2
      className={`mt-4 font-display text-4xl leading-[1.08] tracking-tight md:text-5xl ${dark ? "text-white" : "text-ink"}`}
      data-testid={testId ? `${testId}-title` : undefined}
    >
      {title}
    </h2>
    {description && (
      <p
        className={`mt-5 text-base leading-relaxed md:text-lg ${dark ? "text-white/65" : "text-ink/65"}`}
        data-testid={testId ? `${testId}-description` : undefined}
      >
        {description}
      </p>
    )}
  </Reveal>
);

export const PrimaryLink = ({ to, children, testId, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-sm bg-brand px-7 py-3.5 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:bg-brand-light ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
  </Link>
);

export const OutlineLink = ({ to, children, testId, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-sm border border-ink/25 px-7 py-3.5 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-white ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
  </Link>
);

export const OutlineLinkLight = ({ to, children, testId, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-sm border border-white/35 px-7 py-3.5 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:border-white hover:bg-white hover:text-ink ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
  </Link>
);

export const FocusCard = ({ overline, title, copy, features, image, imageAlt, cta, to, testId, delay = 0 }) => (
  <Reveal delay={delay}>
    <article
      data-testid={testId}
      className="group flex h-full flex-col border border-ink/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-500 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
    >
      <div className="overflow-hidden">
        <img
          src={image}
          alt={imageAlt}
          loading="lazy"
          className="h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 md:h-72"
        />
      </div>
      <div className="flex flex-1 flex-col p-8 lg:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">{overline}</p>
        <h3 className="mt-3 font-display text-3xl leading-tight text-ink">{title}</h3>
        <p className="mt-4 text-base leading-relaxed text-ink/65">{copy}</p>
        <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-ink/75">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-brand" aria-hidden="true" />
              {f}
            </li>
          ))}
        </ul>
        <div className="mt-8 pt-2">
          <Link
            to={to}
            data-testid={`${testId}-cta`}
            className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:text-brand-dark"
          >
            {cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </article>
  </Reveal>
);

export const ApproachSteps = ({ steps, columns = "lg:grid-cols-5" }) => (
  <ol className={`grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 ${columns}`}>
    {steps.map((step, i) => (
      <li key={step.title} className="bg-white">
        <Reveal delay={i * 0.08} className="flex h-full flex-col p-8">
          <span className="font-display text-5xl font-light text-brand">{step.num}</span>
          <h3 className="mt-5 font-display text-2xl text-ink">{step.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-ink/60">{step.description}</p>
        </Reveal>
      </li>
    ))}
  </ol>
);

export const OpportunityCards = () => (
  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
    <Reveal>
      <article data-testid="landowner-cta-card" className="flex h-full flex-col border border-ink/10 bg-white p-8 lg:p-12">
        <LandPlot className="h-9 w-9 text-brand-dark" strokeWidth={1.5} aria-hidden="true" />
        <h3 className="mt-6 font-display text-3xl text-ink">For Landowners</h3>
        <p className="mt-4 flex-1 text-base leading-relaxed text-ink/65">
          Have land with development potential? We would be glad to understand your property and discuss how it could be thoughtfully developed.
        </p>
        <div className="mt-8">
          <PrimaryLink to="/contact" testId="discuss-property-button">Discuss Your Property</PrimaryLink>
        </div>
      </article>
    </Reveal>
    <Reveal delay={0.1}>
      <article data-testid="opportunity-cta-card" className="flex h-full flex-col border border-white/10 bg-olive p-8 text-white lg:p-12">
        <Building2 className="h-9 w-9 text-brand-light" strokeWidth={1.5} aria-hidden="true" />
        <h3 className="mt-6 font-display text-3xl">For Redevelopment Opportunities</h3>
        <p className="mt-4 flex-1 text-base leading-relaxed text-white/70">
          Have a redevelopment or joint-development opportunity? Share the details with us and our team will evaluate it carefully.
        </p>
        <div className="mt-8">
          <PrimaryLink to="/contact" testId="submit-opportunity-button">Submit an Opportunity</PrimaryLink>
        </div>
      </article>
    </Reveal>
  </div>
);

export const CTABanner = ({
  overline = "Evaya Ventures",
  title = "From Earth to Extraordinary.",
  copy = "Every opportunity begins with potential. Our vision is to transform that potential into places, possibilities and value for the future.",
}) => (
  <section className="border-t border-white/10 bg-ink">
    <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-12 lg:py-28">
      <Reveal className="max-w-2xl">
        <span className="block h-px w-16 bg-brand" aria-hidden="true" />
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.28em] text-brand-light">{overline}</p>
        <h2 className="mt-4 font-display text-4xl leading-tight tracking-tight text-white md:text-5xl">{title}</h2>
        <p className="mt-4 text-base leading-relaxed text-white/65 md:text-lg">{copy}</p>
      </Reveal>
      <Reveal delay={0.1}>
        <PrimaryLink to="/contact" testId="cta-banner-button">Talk to Evaya Ventures</PrimaryLink>
      </Reveal>
    </div>
  </section>
);
